import { Autorenew } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PRIMARY } from '../theme/palette';

const PageLoader = ({
  icon: Icon = Autorenew,
  message,
  subMessage,
  size = 80,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      minHeight='400px'
      flexDirection='column'
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: theme =>
          theme.palette.mode === 'dark'
            ? 'rgba(10, 14, 39, 0.95)'
            : 'rgba(248, 249, 255, 0.95)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Box sx={{ position: 'relative', mb: 3 }}>
        <motion.div
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.8, 1, 0.8],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <CircularProgress
            size={size}
            thickness={3}
            sx={{
              color: 'primary.main',
            }}
          />
        </motion.div>
        {Icon && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 360],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <Icon sx={{ fontSize: size * 0.4, color: 'primary.main' }} />
            </motion.div>
          </Box>
        )}
      </Box>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        <Typography
          variant='h5'
          color='textPrimary'
          sx={{
            mb: 1,
            fontWeight: 600,
            background: PRIMARY.gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {message || t('common.loading')}
        </Typography>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <Typography variant='body2' color='textSecondary'>
          {subMessage || t('common.pleaseWait')}
        </Typography>
      </motion.div>

      {/* Animated dots */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.6 }}
        style={{ marginTop: 16 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          {[0, 1, 2].map(index => (
            <motion.div
              key={index}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: index * 0.2,
                ease: 'easeInOut',
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                }}
              />
            </motion.div>
          ))}
        </Box>
      </motion.div>
    </Box>
  );
};

export default PageLoader;
